import { useEffect, useRef, useCallback, useState } from 'react';
import { getOptimalVideoSettings, optimizeVideoElement } from '../utils/videoOptimizations';

/**
 * Custom hook for managing video playback based on visibility
 * Uses Intersection Observer API for optimal performance
 */
const useVideoVisibility = (options = {}) => {
  const {
    threshold = 0.5,           // 50% visibility required to play
    rootMargin = '50px',       // Start observing 50px before visible
    pauseDelay = 100,          // Debounce pause action (ms)
    resumeDelay = 50,          // Debounce resume action (ms)
    respectMotionPrefs = true, // Honor prefers-reduced-motion
    enableSmartPreload = true  // Enable smart preloading
  } = options;

  const videoRef = useRef(null);
  const observerRef = useRef(null);
  const pauseTimeoutRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [videoSettings, setVideoSettings] = useState(null);

  // Check if user prefers reduced motion
  const prefersReducedMotion = useCallback(() => {
    if (!respectMotionPrefs) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, [respectMotionPrefs]);

  // Clear timeouts
  const clearTimeouts = useCallback(() => {
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }, []);

  // Play video with error handling and optimization checks
  const playVideo = useCallback(async () => {
    if (!videoRef.current || prefersReducedMotion()) return;

    // Check if video should play based on optimizations
    if (videoSettings && !videoSettings.shouldPlay) {
      return;
    }

    try {
      clearTimeouts();
      
      resumeTimeoutRef.current = setTimeout(async () => {
        try {
          await videoRef.current.play();
          setIsPlaying(true);
          setHasError(false);
        } catch (error) {
          setHasError(true);
          setIsPlaying(false);
        }
      }, resumeDelay);
    } catch (error) {
      setHasError(true);
    }
  }, [clearTimeouts, resumeDelay, prefersReducedMotion, videoSettings]);

  // Pause video
  const pauseVideo = useCallback(() => {
    if (!videoRef.current) return;

    try {
      clearTimeouts();
      
      pauseTimeoutRef.current = setTimeout(() => {
        if (videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause();
          setIsPlaying(false);
        }
      }, pauseDelay);
    } catch (error) {
      // Silent error handling
    }
  }, [clearTimeouts, pauseDelay]);

  // Handle intersection changes
  const handleIntersection = useCallback((entries) => {
    const [entry] = entries;
    const visible = entry.isIntersecting;
    
    setIsVisible(visible);

    if (visible) {
      playVideo();
    } else {
      pauseVideo();
    }
  }, [playVideo, pauseVideo]);

  // Initialize video optimization settings
  useEffect(() => {
    const initializeVideoSettings = async () => {
      try {
        const settings = await getOptimalVideoSettings();
        setVideoSettings(settings);
      } catch (error) {
        // Silent error handling
      }
    };

    initializeVideoSettings();
  }, []);

  // Initialize Intersection Observer
  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;

    // Optimize video element
    optimizeVideoElement(video);

    // Create observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin
    });

    // Start observing
    observerRef.current.observe(video);

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      clearTimeouts();
    };
  }, [handleIntersection, threshold, rootMargin, clearTimeouts]);

  // Handle video events
  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;

    const handleLoadedData = () => {
      setIsLoaded(true);
      setHasError(false);
    };

    const handleError = () => {
      setHasError(true);
      setIsPlaying(false);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    // Add event listeners
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('error', handleError);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    // Cleanup
    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, []);

  // Smart preload effect based on optimization settings
  useEffect(() => {
    if (!enableSmartPreload || !videoRef.current || !videoSettings) return;

    const video = videoRef.current;
    video.preload = videoSettings.preload;
  }, [enableSmartPreload, videoSettings]);

  return {
    videoRef,
    isVisible,
    isPlaying,
    isLoaded,
    hasError,
    prefersReducedMotion: prefersReducedMotion()
  };
};

export default useVideoVisibility;